import React, { useState, useEffect } from "react";
import axios from "axios";
import { Split, Plus, Trash2, Percent, DollarSign, GripVertical } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Card de Rateio Automático de Capitação.
 * Quando uma mensalidade é paga, o valor é dividido automaticamente entre os CCs.
 */
export const RateioRulesCard = ({ costCenters: costCentersProp = [], canManage = true, capitationValue = 0 }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRule, setNewRule] = useState({ cost_center_id: "", kind: "fixo", value: "" });
  // Se o prop chegou vazio, busca os CCs por conta própria (fallback resiliente)
  const [costCentersLocal, setCostCentersLocal] = useState([]);
  const costCenters = (costCentersProp && costCentersProp.length > 0) ? costCentersProp : costCentersLocal;

  const load = async () => {
    try {
      setLoading(true);
      const [rulesRes, ccsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/treasury/rateio-rules`),
        axios.get(`${BACKEND_URL}/api/treasury/cost-centers`).catch(() => ({ data: [] })),
      ]);
      setRules(rulesRes.data.rules || []);
      if (!costCentersProp || costCentersProp.length === 0) {
        setCostCentersLocal(ccsRes.data || []);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addRule = async () => {
    if (!newRule.cost_center_id || !newRule.value) {
      toast.error("Selecione o Centro de Custo e informe o valor");
      return;
    }
    const value = parseFloat(newRule.value);
    if (isNaN(value) || value <= 0) {
      toast.error("Valor inválido");
      return;
    }
    if (newRule.kind === "percentual" && value > 100) {
      toast.error("Percentual não pode passar de 100");
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/treasury/rateio-rules`, {
        cost_center_id: newRule.cost_center_id,
        kind: newRule.kind,
        value,
        sort_order: rules.length + 1,
        is_active: true,
      });
      toast.success("Regra de rateio adicionada");
      setNewRule({ cost_center_id: "", kind: "fixo", value: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar regra");
    }
  };

  const removeRule = async (ruleId) => {
    if (!window.confirm("Remover esta regra de rateio? Pagamentos futuros deixarão de usá-la.")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/treasury/rateio-rules/${ruleId}`);
      toast.success("Regra removida");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover regra");
    }
  };

  const ccName = (id) => {
    const c = costCenters.find((c) => c.id === id);
    return c ? `${c.code} — ${c.name}` : "—";
  };

  // Mostra TODOS os CCs no dropdown; marca os já em uso como desabilitados/informativos
  const usedIds = new Set(rules.map((r) => r.cost_center_id));
  const availableCount = (costCenters || []).filter((c) => !usedIds.has(c.id)).length;

  // Somatório: soma fixos + calcula % sobre a Capitação
  const capVal = Number(capitationValue) || 0;
  const totalAllocated = rules.reduce((s, r) => {
    if (r.kind === "percentual") return s + (capVal * (Number(r.value) || 0) / 100);
    return s + (Number(r.value) || 0);
  }, 0);
  const diff = Math.round((capVal - totalAllocated) * 100) / 100;
  const isValid = capVal > 0 && Math.abs(diff) < 0.01;
  const isOver = capVal > 0 && diff < -0.01;
  const isUnder = capVal > 0 && diff > 0.01;

  return (
    <div className="luxury-card p-6 space-y-4" data-testid="rateio-rules-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif font-bold text-base text-white flex items-center space-x-2">
            <Split className="w-4 h-4 text-[#D4AF37]" />
            <span>Rateio Automático da Capitação</span>
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Toda mensalidade recebida é dividida automaticamente entre os Centros de Custo configurados abaixo.
          </p>
        </div>
        {rules.length > 0 && (
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30">
            {rules.length} regra{rules.length > 1 ? "s" : ""} ativa{rules.length > 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Somatório vs Capitação */}
      {capVal > 0 && rules.length > 0 && (
        <div className={`p-3 rounded-xl border ${
          isValid
            ? "bg-emerald-500/5 border-emerald-500/40"
            : isOver
              ? "bg-rose-500/5 border-rose-500/40"
              : "bg-amber-500/5 border-amber-500/40"
        }`}>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-300 font-semibold">
              Composição atual:
            </span>
            <div className="flex items-center gap-3 font-mono">
              <span className="text-slate-400">
                <span className="text-white font-bold">R$ {totalAllocated.toFixed(2).replace(".", ",")}</span>
                <span className="text-slate-500 mx-1">/</span>
                <span className="text-[#D4AF37] font-bold">R$ {capVal.toFixed(2).replace(".", ",")}</span>
              </span>
              {isValid && (
                <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/50">
                  ✓ Confere
                </span>
              )}
              {isOver && (
                <span className="text-[10px] uppercase tracking-widest text-rose-300 font-bold px-2 py-0.5 rounded bg-rose-500/20 border border-rose-500/50">
                  Excede R$ {Math.abs(diff).toFixed(2).replace(".", ",")}
                </span>
              )}
              {isUnder && (
                <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold px-2 py-0.5 rounded bg-amber-500/20 border border-amber-500/50">
                  🔒 R$ {diff.toFixed(2).replace(".", ",")} → Multa (se atraso)
                </span>
              )}
            </div>
          </div>
          {isUnder && (
            <p className="text-[10px] text-slate-500 italic mt-2 leading-relaxed">
              💡 A diferença de <b className="text-amber-300 font-mono">R$ {diff.toFixed(2).replace(".", ",")}</b> corresponde ao desconto de pontualidade. Quando o Irmão pagar <b>após o vencimento</b> (sem desconto), esse valor vai automaticamente para o CC imutável <b className="text-rose-300">"Multa por atraso"</b>.
            </p>
          )}
        </div>
      )}

      {/* Lista de regras existentes */}
      {loading ? (
        <div className="text-xs text-slate-500 italic">Carregando…</div>
      ) : rules.length === 0 ? (
        <div className="p-4 rounded-xl border border-dashed border-slate-700 bg-[#080C14] text-center">
          <p className="text-xs text-slate-400 mb-1">Nenhum rateio configurado.</p>
          <p className="text-[10px] text-slate-500 italic">Sem regras, todo pagamento vai integral para o CC padrão.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((r) => (
            <div
              key={r.id}
              data-testid={`rateio-rule-row-${r.id}`}
              className="flex items-center justify-between gap-2 p-3 rounded-xl bg-[#080C14] border border-slate-800 hover:border-[#D4AF37]/40 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <GripVertical className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-semibold truncate">{ccName(r.cost_center_id)}</div>
                  <div className="text-[10px] text-slate-500 truncate">{r.cost_center_id}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.kind === "percentual" ? (
                    <Percent className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <DollarSign className="w-3.5 h-3.5 text-[#D4AF37]" />
                  )}
                  <span className="font-mono font-bold text-sm text-white">
                    {r.kind === "percentual"
                      ? `${r.value}%`
                      : `R$ ${Number(r.value).toFixed(2).replace(".", ",")}`}
                  </span>
                </div>
              </div>
              {canManage && (
                <button
                  onClick={() => removeRule(r.id)}
                  data-testid={`btn-remove-rateio-${r.id}`}
                  title="Remover regra"
                  className="p-1.5 rounded-md text-rose-400 hover:bg-rose-500/10 shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Form de nova regra */}
      {canManage && (
        <div className="p-4 rounded-xl bg-[#131C2E] border border-[#D4AF37]/30">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3">
            Adicionar nova regra
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <select
              data-testid="rateio-new-cc"
              value={newRule.cost_center_id}
              onChange={(e) => setNewRule({ ...newRule, cost_center_id: e.target.value })}
              className="col-span-2 bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white"
            >
              <option value="">
                {availableCount === 0
                  ? `Todos os ${(costCenters || []).length} CCs já possuem regra`
                  : `Selecione o CC (${availableCount} disponíve${availableCount === 1 ? "l" : "is"} de ${(costCenters || []).length})…`}
              </option>
              {(costCenters || []).map((c) => {
                const inUse = usedIds.has(c.id);
                return (
                  <option key={c.id} value={c.id} disabled={inUse}>
                    {c.code} — {c.name}{inUse ? "  •  já configurado" : ""}
                  </option>
                );
              })}
            </select>
            <select
              data-testid="rateio-new-kind"
              value={newRule.kind}
              onChange={(e) => setNewRule({ ...newRule, kind: e.target.value })}
              className="bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white"
            >
              <option value="fixo">R$ Fixo</option>
              <option value="percentual">%</option>
            </select>
            <div className="flex gap-2">
              <input
                data-testid="rateio-new-value"
                type="number"
                step="0.01"
                min="0"
                max={newRule.kind === "percentual" ? "100" : undefined}
                placeholder={newRule.kind === "percentual" ? "% (ex: 10)" : "R$ (ex: 25)"}
                value={newRule.value}
                onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                className="flex-1 bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-xs text-white font-mono"
              />
              <button
                data-testid="btn-add-rateio"
                onClick={addRule}
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-3 rounded-lg text-xs uppercase transition-all"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-[10px] text-slate-500 italic mt-2">
            💡 Dica: use "R$ Fixo" para valores obrigatórios (ex: Contribuição Associativa) e "%" para gordura variável.
          </p>
        </div>
      )}
    </div>
  );
};

export default RateioRulesCard;
